import React, { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Stack, Grid, Paper, useTheme, Fade, IconButton, Tooltip, Avatar } from '@mui/material';
import useDataFetch from '../../utils/useDataFetch';
import { useAuth } from '../../context/AuthContext';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import MemberList from '../../components/teams/MemberList';
import AttendanceStats from '../../components/teams/AttendanceStats';
import EditMemberDialog from '../../components/teams/EditMemberDialog';

const TeamOverview = () => {
    const theme = useTheme();
    const { teamId } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const [tab, setTab] = useState('members');
    const [selectedMember, setSelectedMember] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const fileInputRef = useRef(null);

    const { data: teamInfoData, refetch: refetchTeamInfo } = useDataFetch({ url: `/teams/info/${teamId}` });
    const { data: membersData, isLoading: mLoading, refetch: refetchMembers } = useDataFetch({ url: `/teams/${teamId}/members` });
    const { data: statsData, isLoading: sLoading } = useDataFetch({ url: `/stats/team/${teamId}/attendance` });

    const team = teamInfoData?.team;
    const members = membersData?.members || [];
    const stats = statsData?.stats || [];

    const avgParticipation = stats.length > 0
        ? Math.round((stats.reduce((userAcc, member) => {
            const counts = Object.values(member.counts || {});
            const totalEvents = counts.reduce((countAcc, item) => countAcc + (item.total || 0), 0);
            const attended = counts.reduce((countAcc, item) => countAcc + (item.attended || 0), 0);
            return userAcc + (totalEvents > 0 ? attended / totalEvents : 0);
        }, 0) / stats.length) * 100)
        : 0;

    const currentMember = useMemo(
        () => members.find(m => m.id === user?.id),
        [members, user]
    );

    if (mLoading || sLoading) return (
        <CircularProgress />
    );

    const canEditMembers = currentMember?.team_role === 'trainer' || user?.role === 'super_admin' || user?.isAdmin;

    const handleProfileClick = (member) => {
        navigate(`/users/${member.id}`);
    };

    const handleEditClick = (member) => {
        if (!canEditMembers) return;
        setSelectedMember(member);
        setIsEditOpen(true);
    };

    const handleCloseEdit = () => {
        setIsEditOpen(false);
        setSelectedMember(null);
    };

    const handleSaveEdit = () => {
        refetchMembers();
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/teams/${teamId}/logo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                refetchTeamInfo();
            } else {
                alert(data.message || 'Fehler beim Upload');
            }
        } catch (error) {
            console.error(error);
            alert('Upload fehlgeschlagen');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 8 }, maxWidth: 1100, mx: 'auto', mb: "64px" }}>
            {/* Header Section */}
            <Fade in timeout={800}>
                <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box>
                            <Typography variant="h2" sx={{ fontWeight: 900, color: 'text.primary', mb: 1, letterSpacing: '-0.04em' }}>
                                {team?.name || 'Mannschaft'}<span style={{ color: theme.palette.primary.main }} />
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box sx={{ width: 8, height: 8, borderRadius: "12px", bgcolor: 'primary.main', boxShadow: '0 0 10px rgba(190, 242, 100, 0.5)' }} />
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600, fontFamily: '"Fira Code", monospace' }}>
                                    {members.length} Mitglieder
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/teams/${teamId}/treasury`)}
                            startIcon={<AccountBalanceWalletRoundedIcon />}
                            sx={{
                                px: 4,
                                py: 1.8,
                                borderRadius: "12px",
                                fontWeight: 900,
                                bgcolor: '#f2b903',
                                color: '#000',
                                boxShadow: `0 10px 25px rgba(190, 242, 100, 0.25)`,
                                '&:hover': { bgcolor: '#f2b903', transform: 'scale(1.02)' },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            Teamkasse
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/teams/${teamId}/gallery`)}
                            startIcon={<PhotoLibraryRoundedIcon />}
                            sx={{
                                px: 4,
                                py: 1.8,
                                borderRadius: "12px",
                                fontWeight: 900,
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', transform: 'scale(1.02)' },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            Galerie
                        </Button>
                        <Tooltip title="Team-Einstellungen">
                            <IconButton sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: "12px", p: 2 }} onClick={() => alert("Gibts noch nicht")}>
                                <SettingsRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Fade>

            {/* Bento Summary Grid */}
            <Grid container spacing={3} sx={{ mb: 8 }} sx={{ width: '100%' }}>

                <Grid sx={{ width: '100%' }}>
                    <Paper
                        sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: '1px solid',
                            borderColor: 'primary.main',
                            background: 'linear-gradient(135deg, rgba(190, 242, 100, 0.1) 0%, rgba(190, 242, 100, 0.02) 100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: 120,
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5, color: 'primary.main' }}>
                                    {avgParticipation}<span style={{ fontSize: '1.5rem' }}>%</span>
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                    Ø Beteiligung
                                </Typography>
                            </Box>
                            <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: 'primary.main', color: '#000' }}>
                                <BarChartRoundedIcon fontSize="large" />
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Navigation Switcher */}
            <Box sx={{ mb: 2, mt: 4, display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 1, borderRadius: "12px", display: 'inline-flex', border: '1px solid', borderColor: 'divider', width: '100%' }}>
                    <Button
                        onClick={() => setTab('members')}
                        sx={{
                            borderRadius: "12px",
                            px: 5,
                            py: 1.5,
                            bgcolor: tab === 'members' ? 'primary.main' : 'transparent',
                            color: tab === 'members' ? '#000' : 'text.primary',
                            fontWeight: 900,
                            '&:hover': { bgcolor: tab === 'members' ? 'primary.dark' : 'rgba(255,255,255,0.08)' },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        startIcon={<PeopleAltRoundedIcon />}
                    >
                        Mitglieder
                    </Button>
                    <Button
                        onClick={() => setTab('stats')}
                        sx={{
                            borderRadius: "12px",
                            px: 5,
                            py: 1.5,
                            bgcolor: tab === 'stats' ? 'primary.main' : 'transparent',
                            color: tab === 'stats' ? '#000' : 'text.primary',
                            fontWeight: 900,
                            '&:hover': { bgcolor: tab === 'stats' ? 'primary.dark' : 'rgba(255,255,255,0.08)' },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        startIcon={<LeaderboardRoundedIcon />}
                    >
                        Analyse
                    </Button>
                </Box>
            </Box>

            {/* Content Area */}
            <Fade in={true} key={tab} timeout={500}>
                <Box>
                    {tab === 'members'
                        ? <MemberList
                            members={members}
                            onProfileClick={handleProfileClick}
                            onEditClick={handleEditClick}
                            canEdit={canEditMembers}
                        />
                        : <AttendanceStats stats={stats} />
                    }
                </Box>
            </Fade>

            <EditMemberDialog
                open={isEditOpen}
                member={selectedMember}
                teamId={teamId}
                onClose={handleCloseEdit}
                onSaved={handleSaveEdit}
            />
        </Box>
    );
};

export default TeamOverview;
